<template>
  <button type="button" class="btn btn-outline-primary" @click="open">
    <i class="bi bi-search me-2"></i>Buscar
  </button>

  <div class="modal fade" id="modal_search_rental" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-search me-2"></i>Buscar locação</h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <div class="field mb-3">
            <label for="inpBuscaCodigo" class="field__label">Código da locação</label>
            <div class="d-flex gap-2">
              <input
                v-model="code"
                id="inpBuscaCodigo"
                class="field__input"
                type="text"
                placeholder="Ex: 126GB94N"
                :disabled="loading"
                @keydown.enter.prevent="buscarPorCodigo"
              />
              <button type="button" class="btn btn-primary text-nowrap" :disabled="loading" @click="buscarPorCodigo">
                <span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
                <span v-else>Buscar</span>
              </button>
            </div>
          </div>

          <div class="search-divider"><span>ou</span></div>

          <div class="text-center">
            <button v-if="!scanning" type="button" class="btn btn-secondary" @click="startScan">
              <i class="bi bi-qr-code-scan me-2"></i>Ler QR Code
            </button>
            <button v-else type="button" class="btn btn-outline-secondary" @click="stopScan">
              <i class="bi bi-x-circle me-2"></i>Parar leitura
            </button>
          </div>

          <div v-show="scanning" class="qr-scan-wrapper mt-3">
            <video ref="videoEl" class="qr-scan-video"></video>
          </div>

          <p v-if="scanError" class="text-danger small mt-3 mb-0">{{ scanError }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onBeforeUnmount, nextTick } from 'vue';
import { Modal } from 'bootstrap';
import QrScanner from 'qr-scanner';
import { rental } from '@/models/rental';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';
import router from '@/router';

export default {
  name: 'RentalSearch',
  setup() {
    const code = ref('');
    const loading = ref(false);
    const scanning = ref(false);
    const scanError = ref('');
    const videoEl = ref(null);

    let modalInstance = null;
    let qrScanner = null;

    const toastStore = useToastStore();
    const model = rental();

    function open() {
      const el = document.getElementById('modal_search_rental');
      modalInstance = new Modal(el);
      el.addEventListener('hidden.bs.modal', stopScan, { once: true });
      modalInstance.show();
    }

    function close() {
      modalInstance?.hide();
    }

    function resetState() {
      code.value = '';
      scanError.value = '';
    }

    async function irParaLocacao(id) {
      close();
      stopScan();
      resetState();
      await router.push(`/rental/details?id=${id}`);
    }

    async function buscarCodigo(value) {
      const codigo = value.trim();
      if (!codigo) {
        toastStore.warning('Digite ou escaneie o código da locação.');
        return;
      }

      loading.value = true;
      try {
        const result = await model.findByCode(codigo);
        await irParaLocacao(result.id);
      } catch (error) {
        toastStore.error('Locação não encontrada: ' + parseError(error));
      } finally {
        loading.value = false;
      }
    }

    function buscarPorCodigo() {
      buscarCodigo(code.value);
    }

    async function startScan() {
      scanError.value = '';
      scanning.value = true;
      await nextTick(); // garante que o <video> já está renderizado

      try {
        qrScanner = new QrScanner(
          videoEl.value,
          (result) => {
            const texto = typeof result === 'string' ? result : result.data;
            stopScan();
            buscarCodigo(texto);
          },
          { highlightScanRegion: true, highlightCodeOutline: true }
        );
        await qrScanner.start();
      } catch (error) {
        scanError.value = 'Não foi possível acessar a câmera. Verifique a permissão do navegador ou digite o código manualmente.';
        scanning.value = false;
      }
    }

    function stopScan() {
      if (qrScanner) {
        qrScanner.stop();
        qrScanner.destroy();
        qrScanner = null;
      }
      scanning.value = false;
    }

    onBeforeUnmount(stopScan);

    return {
      code, loading, scanning, scanError, videoEl,
      open, close, buscarPorCodigo, startScan, stopScan,
    };
  }
}
</script>

<style scoped>
.search-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #9ca3af;
  font-size: 0.8rem;
  margin: 12px 0;
}
.search-divider::before,
.search-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e5e7eb;
}
.search-divider span {
  padding: 0 10px;
}

.qr-scan-video {
  width: 100%;
  max-height: 320px;
  border-radius: 8px;
  background: #000;
  object-fit: cover;
}
</style>
